import { EVIDENCE_CLASS, REC_CLASS, REC_LABEL, RUN_STATUS_CLASS, STATUS_CLASS, titleCase } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { AlertCircle, ExternalLink } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

export const PageHeader = ({ title, subtitle, actions, testId }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 fade-up" data-testid={testId}>
    <div>
      <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
  </div>
);

export const RecBadge = ({ value, testId }) => value ? <span className={`chip ${REC_CLASS[value] || "chip-muted"}`} data-testid={testId}>{REC_LABEL[value] || titleCase(value)}</span> : <span className="chip chip-muted">Not assessed</span>;
export const EvidenceBadge = ({ value }) => <span className={`chip ${EVIDENCE_CLASS[value] || "chip-muted"}`}>{titleCase(value)}</span>;
export const TypeBadge = ({ value }) => <span className={`chip ${value === "fact" ? "chip-muted" : "chip-warning"}`}>{value === "fact" ? "Fact" : "Inference"}</span>;
export const StatusBadge = ({ value, map = STATUS_CLASS }) => <span className={`chip ${map[value] || "chip-muted"}`}>{titleCase(value)}</span>;
export const RunStatusBadge = ({ value }) => <StatusBadge value={value} map={RUN_STATUS_CLASS} />;
export const ContactStatusBadge = ({ value }) => {
  const cls = { verified: "chip-success", published: "chip-info", licensed_provider: "chip-info", uncertain: "chip-warning", not_found: "chip-muted" }[value] || "chip-muted";
  return <span className={`chip ${cls}`}>{titleCase(value)}</span>;
};

const LEVEL_W = { low: "w-1/3", medium: "w-2/3", high: "w-full" };
export function Assessment({ a, compact = false }) {
  if (!a) return <span className="meta">No assessment yet</span>;
  const rows = [["Fit", a.fit], ["Timing", a.timing], ["Relationship", a.relationship], ["Evidence quality", a.evidence_quality]];
  return (
    <div className={compact ? "grid grid-cols-4 gap-2" : "space-y-1.5"} data-testid="assessment">
      {rows.map(([label, lvl]) => (
        <div key={label} className={compact ? "" : "flex items-center gap-2 text-xs"}>
          <span className={`${compact ? "meta block mb-0.5" : "w-28 text-muted-foreground"}`}>{label}</span>
          <div className={`${compact ? "" : "flex-1"} h-1.5 rounded-full bg-muted overflow-hidden`}>
            <div className={`h-full rounded-full ${LEVEL_W[lvl] || "w-0"} ${label === "Evidence quality" && lvl === "low" ? "bg-[hsl(var(--warning))]" : "bg-primary/70"}`} />
          </div>
          {!compact && <span className="w-14 text-right capitalize text-muted-foreground">{lvl}</span>}
        </div>
      ))}
    </div>
  );
}

export function EvidenceList({ items = [], emptyText = "No evidence captured yet." }) {
  if (!items.length) return <p className="meta">{emptyText}</p>;
  return (
    <ul className="space-y-2" data-testid="evidence-list">
      {items.map((e) => (
        <li key={e.id} className="rounded-md border border-border bg-card p-3">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <EvidenceBadge value={e.label} /><TypeBadge value={e.type} />
            {e.relevance && e.relevance !== "general" && <span className="chip chip-muted">{titleCase(e.relevance)}</span>}
          </div>
          <p className={`text-sm ${e.type === "inference" ? "italic" : ""}`}>{e.claim}</p>
          {e.excerpt && <blockquote className="mt-1 text-xs text-muted-foreground border-l-2 border-border pl-2">“{e.excerpt}”</blockquote>}
          <div className="meta mt-1.5 flex items-center gap-2 flex-wrap">
            {e.source_url ? <a href={e.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline truncate max-w-[320px]"><ExternalLink className="h-3 w-3" />{e.source_title || e.source_url}</a> : <span>No source</span>}
            <span>· retrieved {fmtDateTime(e.retrieved_at)}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

export const EmptyState = ({ title, text, action, testId }) => (
  <div className="panel p-8 text-center" data-testid={testId}>
    <h3 className="font-heading font-semibold">{title}</h3>
    {text && <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const LoadingRows = ({ rows = 5 }) => (
  <div className="space-y-2 p-3">{Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
);

export const ErrorBox = ({ error, onRetry }) => (
  <div className="rounded-md border border-[hsl(var(--danger)/0.3)] bg-[hsl(var(--danger-bg))] p-3 text-sm flex items-start gap-2" role="alert" data-testid="error-box">
    <AlertCircle className="h-4 w-4 mt-0.5 text-[hsl(var(--danger))]" />
    <div className="flex-1">{error}</div>
    {onRetry && <Button size="sm" variant="outline" onClick={onRetry}>Retry</Button>}
  </div>
);

export const Field = ({ label, children, hint }) => (
  <label className="block space-y-1">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    {children}
    {hint && <span className="meta block">{hint}</span>}
  </label>
);

export const Stat = ({ label, value, testId }) => (
  <div className="panel px-4 py-3" data-testid={testId}>
    <div className="meta">{label}</div>
    <div className="text-xl font-heading font-semibold nums">{value}</div>
  </div>
);
